import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Bookmark, Bot, FolderOpen, Home, MapPin, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { suburbInsights } from "@/data/suburbInsights";
import { useSession } from "@/components/Account";

const starters = [
  "Help me compare two suburbs",
  "What should a first-home buyer check first?",
  "Explain rental yield in plain English",
  "How should I prepare to sell a property?",
];

const purposes = [
  ["explore", "Just exploring"],
  ["buy", "Buying a home"],
  ["first_home", "First-home buyer"],
  ["invest", "Investing"],
  ["sell", "Selling"],
  ["own", "Owning or reviewing"],
];

export default function AskPropAI() {
  const [searchParams] = useSearchParams();
  const { user } = useSession();
  const initialQuestion = useRef(searchParams.get("question") || "");
  const [purpose, setPurpose] = useState(searchParams.get("purpose") || "explore");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState(starters);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [workspaceTitle, setWorkspaceTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const threadRef = useRef(null);
  const startedFromHome = useRef(false);

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTo({ top: thread.scrollHeight, behavior: "smooth" });
  }, [messages, busy, error]);

  useEffect(() => {
    if (!startedFromHome.current && initialQuestion.current) {
      startedFromHome.current = true;
      send(null, initialQuestion.current);
    }
    // Consume the homepage handoff once; later state changes must not resend it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) {
      setWorkspaces([]);
      return;
    }
    api.get("/research/conversations")
      .then(({ data }) => setWorkspaces(data.conversations || []))
      .catch(() => setWorkspaces([]));
  }, [user]);

  const serialiseMessages = () => messages.map((message) => ({
    role: message.role,
    content: message.content,
    disclaimer: message.disclaimer || null,
    context_used: message.contextUsed || [],
    comparison: message.comparison || null,
  }));

  async function saveWorkspace() {
    if (!user || !messages.length || saving) return;
    setSaving(true);
    setError("");
    const firstQuestion = messages.find((message) => message.role === "user")?.content || "Prop AI conversation";
    const title = (workspaceTitle || firstQuestion).slice(0, 100);
    const payload = { title, purpose, location: location.trim() || null, messages: serialiseMessages() };
    try {
      const { data } = workspaceId
        ? await api.put(`/research/conversations/${workspaceId}`, payload)
        : await api.post("/research/conversations", payload);
      setWorkspaceId(data.id);
      setWorkspaceTitle(title);
      const list = await api.get("/research/conversations");
      setWorkspaces(list.data.conversations || []);
    } catch (_err) {
      setError("This conversation could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function loadWorkspace(id) {
    setError("");
    try {
      const { data } = await api.get(`/research/conversations/${id}`);
      setWorkspaceId(data.id);
      setWorkspaceTitle(data.title);
      setPurpose(data.purpose || "explore");
      setLocation(data.location || "");
      setMessages((data.messages || []).map((message) => ({
        ...message,
        contextUsed: message.context_used || [],
      })));
      setSuggestions(starters);
    } catch (_err) {
      setError("That saved conversation could not be opened.");
    }
  }

  function newWorkspace() {
    setWorkspaceId(null);
    setWorkspaceTitle("");
    setMessages([]);
    setSuggestions(starters);
    setError("");
  }

  async function deleteWorkspace(id) {
    if (!window.confirm("Delete this saved conversation? This cannot be undone.")) return;
    try {
      await api.delete(`/research/conversations/${id}`);
      setWorkspaces((current) => current.filter((item) => item.id !== id));
      if (workspaceId === id) newWorkspace();
    } catch (_err) {
      setError("That saved conversation could not be deleted.");
    }
  }

  async function send(event, suggestedQuestion) {
    event?.preventDefault();
    const nextQuestion = (suggestedQuestion || question).trim();
    if (nextQuestion.length < 2 || busy) return;
    const history = messages.slice(-10).map(({ role, content }) => ({
      role,
      content: content.slice(0, 5000),
    }));
    setMessages((current) => [...current, { role: "user", content: nextQuestion }]);
    setQuestion("");
    setBusy(true);
    setError("");
    try {
      const placeText = `${nextQuestion} ${location}`.toLowerCase();
      const places = suburbInsights
        .filter((item) => placeText.includes(item.name.toLowerCase()))
        .slice(0, 2)
        .map((item) => item.slug);
      const { data } = await api.post("/research/general-assistant", {
        question: nextQuestion,
        purpose,
        location: location.trim() || null,
        history,
        places,
      });
      setMessages((current) => [
        ...current,
        { role: "assistant", content: data.answer, disclaimer: data.disclaimer, contextUsed: data.context_used || [], comparison: data.comparison || null },
      ]);
      setSuggestions(data.suggested_followups || []);
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Prop AI could not respond right now. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="general-assistant-page">
      <aside className="general-assistant-sidebar">
        <span className="eyebrow">Your property co-pilot</span>
        <h1>Ask Prop AI.</h1>
        <p>
          Start anywhere—buying, investing, selling, owning, comparing a suburb
          or simply learning how property works.
        </p>
        <label>
          What are you thinking about?
          <select value={purpose} onChange={(event) => setPurpose(event.target.value)}>
            {purposes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span><MapPin size={16} aria-hidden="true" /> Suburb or region <small>(optional)</small></span>
          <input
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            maxLength={160}
            placeholder="Any Australian suburb"
          />
        </label>
        <div className="assistant-scope-card">
          <Sparkles size={18} aria-hidden="true" />
          <p><strong>No address required.</strong> General questions work Australia-wide. Exact property findings still use verified sources in Research.</p>
        </div>
        <Link to="/research" className="text-link">
          <Home size={17} aria-hidden="true" /> Research an exact property <ArrowRight size={16} />
        </Link>
        <div className="conversation-workspaces">
          <div><strong><FolderOpen size={15} /> Workspaces</strong><button type="button" onClick={newWorkspace} aria-label="Start a new conversation"><Plus size={15} /></button></div>
          {!user && <p><Link to="/account">Sign in</Link> to save and continue conversations.</p>}
          {user && workspaces.length === 0 && <p>Your saved conversations will appear here.</p>}
          {user && workspaces.map((workspace) => <article key={workspace.id} className={workspace.id === workspaceId ? "active" : ""}>
            <button type="button" onClick={() => loadWorkspace(workspace.id)}><span>{workspace.title}</span><small>{workspace.purpose?.replace("_", " ")} · {new Date(workspace.updated_at).toLocaleDateString("en-AU")}</small></button>
            <button type="button" onClick={() => deleteWorkspace(workspace.id)} aria-label={`Delete ${workspace.title}`}><Trash2 size={14} /></button>
          </article>)}
        </div>
      </aside>

      <section className="general-chat" aria-labelledby="general-chat-heading">
        <header>
          <div className="assistant-avatar"><Bot size={23} aria-hidden="true" /></div>
          <div>
            <h2 id="general-chat-heading">Prop AI</h2>
            <span><i /> Online · general property guidance</span>
          </div>
          <div className="chat-save-actions">
            <button type="button" onClick={newWorkspace}><Plus size={15} /> New</button>
            {user ? <button type="button" onClick={saveWorkspace} disabled={!messages.length || saving}><Bookmark size={15} /> {saving ? "Saving…" : workspaceId ? "Update" : "Save"}</button> : <Link to="/account"><Bookmark size={15} /> Sign in to save</Link>}
          </div>
        </header>

        <div className="general-chat-thread" aria-live="polite" ref={threadRef}>
          {messages.length === 0 && (
            <div className="general-chat-welcome">
              <span className="eyebrow">A better place to begin</span>
              <h2>What would you like to understand?</h2>
              <p>You do not need to know the right terminology. Tell me what you are trying to decide, and we’ll work through it together.</p>
            </div>
          )}
          {messages.map((message, index) => (
            <article key={`${message.role}-${index}`} className={`general-message ${message.role}`}>
              <span>{message.role === "assistant" ? "Prop AI" : "You"}</span>
              <p>{message.content.replace(/\*\*/g, "")}</p>
              {message.contextUsed?.length > 0 && <div className="message-context"><strong>Prop AI evidence used</strong><div>{message.contextUsed.map((name) => {
                const insight = suburbInsights.find((item) => item.name === name);
                if (!insight) return <span className="context-place" key={name}>{name}<small>Queensland suburb snapshot</small></span>;
                return <Link key={name} to={`/suburbs/${insight.slug}`}><span>{name}</span><b>${insight.weeklyRent} / wk</b><small>RTA {insight.postcode} · {insight.rentDwelling} · 2026 Q2</small></Link>;
              })}</div><p>Postcode benchmarks, not property rent appraisals. Open each dashboard for sources and limitations.</p></div>}
              {message.comparison && <div className="assistant-comparison"><div><strong>{message.comparison.title}</strong><span>{message.comparison.period} · {message.comparison.source}</span></div><div className="assistant-comparison-scroll"><table><thead><tr><th>Metric</th>{message.comparison.places.map((place) => <th key={place}>{place}</th>)}</tr></thead><tbody>{message.comparison.rows.map((row) => <tr key={row.metric}><th>{row.metric}</th>{row.values.map((value, valueIndex) => <td key={`${row.metric}-${valueIndex}`}>{value}</td>)}</tr>)}</tbody></table></div><p>{message.comparison.scope}</p></div>}
              {message.disclaimer && <small>{message.disclaimer}</small>}
            </article>
          ))}
          {busy && (
            <article className="general-message assistant thinking">
              <span>Prop AI</span><p>Thinking through your question…</p>
            </article>
          )}
        </div>

        <div className="general-chat-actions" aria-label="Suggested questions">
          {suggestions.map((suggestion) => (
            <button key={suggestion} type="button" onClick={(event) => send(event, suggestion)} disabled={busy}>
              {suggestion}
            </button>
          ))}
        </div>
        {error && <p className="error-state" role="alert">{error}</p>}
        <form className="general-chat-form" onSubmit={send}>
          <label className="sr-only" htmlFor="general-question">Message Prop AI</label>
          <textarea
            id="general-question"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send(event);
              }
            }}
            rows={2}
            maxLength={1200}
            placeholder="Ask anything about buying, investing, selling or owning property…"
          />
          <button disabled={busy || question.trim().length < 2} aria-label="Send message">
            <Send size={19} aria-hidden="true" />
          </button>
        </form>
        <p className="general-chat-privacy">Chats remain temporary unless you explicitly save a workspace while signed in. Avoid sharing sensitive personal information.</p>
      </section>
    </div>
  );
}
